/**
 * What goes in a draft audit report, as plain data. docx-report.ts lays it out in the Ryoma
 * Word template. Figures come only from the platform; the founders write the recommendations.
 *
 * A cover, then five parts of one page each, in the order a client asks the questions:
 *   1. What you spend now
 *   2. Where the extra cost comes from: billing errors to claim back, and costs built into how you pack
 *   3. Where you could save: the five levers, how sure we are, effort and timing
 *   4. The plan: up to 6 weeks, week by week, and what it takes to go ahead
 *   5. Before and after: today's cost against the cost after the savings and all of our fees
 * Every estimate is labelled as one: projections are not promises.
 */
import { annualise, type Lever1ParcelResult, type Lever1Summary, type Lever2ParcelResult, type VersionBlock } from "@ryoma/engine";
import type { AuditRow, LeverRun, Lever2RunSummary, ManualLever, UploadRow } from "../data/db.ts";
import type { Baseline } from "./baseline.ts";
import { COMPANY, MOTTOS, type Motto } from "./brand.ts";
import { IMPLEMENTATION_WEEKS, sortActions, suggestedActions, type ImplementationAction } from "./implementation.ts";
import { LEVERS } from "./levers.ts";
import { COMPLIANCE_CHECKED_ON, EU_EMPTY_SPACE, PACKAGING_EPR, PLASTIC_PACKAGING_TAX, emptySpaceStanding } from "./compliance.ts";

export const RECOMMENDATIONS_PLACEHOLDER = "[FOUNDERS: write the recommendations here before this report is sent.]";
export const REVIEW_PLACEHOLDER = "Reviewed by: [FOUNDERS: name and date before this report is sent]";

export type Block =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "table"; headers: string[]; rows: string[][]; numeric?: number[]; widths?: number[]; small?: boolean }
  | { kind: "note"; lines: string[] }
  | { kind: "motto"; motto: Motto }
  | { kind: "hero"; value: string; label: string }
  /** Horizontal bars, drawn as table cells; value sets the bar length */
  | { kind: "bars"; rows: { label: string; value: number; text: string }[] }
  /** The closing sign-off, beside the "checked" stamp */
  | { kind: "signoff"; lines: string[] };

export interface Section {
  heading: string;
  blocks: Block[];
}

export interface ReportModel {
  /** Used in the footer and file name */
  documentName: string;
  title: string;
  description: string;
  versionLine: string;
  summaryLabel: string;
  summary: string[];
  /** The rest of the cover, under the summary box. The contents list follows it. */
  cover: Block[];
  /** The foot of the cover */
  preparedFor?: string;
  preparedBy?: string;
  contact?: string;
  /** The five parts, one page each, numbered from 1 after the cover */
  sections: Section[];
}

export interface ReportInput {
  audit: AuditRow;
  upload: UploadRow;
  runs: Partial<Record<1 | 2, LeverRun>>;
  manual: ManualLever[];
  actions: ImplementationAction[];
  block: VersionBlock;
  /** The client's starting point, from the cleaned parcels; null if they could not be loaded */
  baseline: Baseline | null;
  /** Drafts already exported for this audit; this one is the next number */
  draftNumber: number;
}

export const gbp = (p: number | null | undefined, dp = 2): string =>
  p === null || p === undefined
    ? "not calculated"
    : `${p < 0 ? "-" : ""}£${(Math.abs(p) / 100).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const whole = (p: number | null | undefined) => (p === null || p === undefined ? "not calculated" : gbp(p, 0));
const n = (x: number) => x.toLocaleString("en-GB");
const pct = (part: number, whole_: number) => (whole_ > 0 ? `${Math.round((part / whole_) * 100)}%` : "–");
const pct1 = (x: number) => `${Math.round(x * 1000) / 10}%`;
const day = (iso: string | null | undefined) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" }) : "not set";
const monthYear = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "Europe/London" });
const plural = (x: number, word: string, many = `${word}s`) => `${n(x)} ${x === 1 ? word : many}`;
const DAY = 864e5;
const toMs = (iso: string) => Date.parse(`${iso.slice(0, 10)}T12:00:00Z`);

export function versionLines(b: VersionBlock): string[] {
  return [
    `Engine version: ${b.engineVersion}`,
    `Data fingerprint (SHA-256): ${b.datasetHash}`,
    `Parcels analysed: ${n(b.parcelCount)}`,
    `Rate cards: ${b.rateCards.join(", ") || "none"}`,
    `Packaging catalogue: ${b.packagingCatalogue ?? "none"}`,
    `Generated: ${b.generatedAt}`,
  ];
}

export function buildVersionBlock(upload: UploadRow, runs: Partial<Record<1 | 2, LeverRun>>, engineVersion: string, generatedAt = new Date().toISOString()): VersionBlock {
  const cards = new Set<string>([...(runs[1]?.rate_cards ?? []), ...(runs[2]?.rate_cards ?? [])]);
  return {
    engineVersion,
    datasetHash: upload.dataset_hash,
    parcelCount: upload.parcel_count,
    rateCards: [...cards].sort(),
    packagingCatalogue: runs[2]?.packaging_catalogue ?? null,
    generatedAt,
  };
}

const OVER_LABEL: Record<string, string> = { lengthCm: "cm on length", widthCm: "cm on width", heightCm: "cm on height", weightKg: "kg on weight" };
const overBy = (o: Record<string, number | undefined>) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${v} ${OVER_LABEL[k] ?? k}`)
    .join(", ");

/** Weeks for a yearly saving to cover a one-off cost; shown in weeks up to 13, then months. */
function payback(oneOff: number, annualNet: number | null): string {
  if (oneOff <= 0) return "straight away";
  if (annualNet === null || annualNet <= 0) return "not within a year";
  const weeks = Math.ceil(oneOff / (annualNet / 52));
  if (weeks > 52) return "not within a year";
  return weeks <= 13 ? plural(weeks, "week") : `${Math.round((weeks / 52) * 12 * 10) / 10} months`;
}

/** Levers 1 and 2 from the runs on the current data, and the total a year across all five levers. */
export function savingTotals(upload: UploadRow, runs: Partial<Record<1 | 2, LeverRun>>, manual: ManualLever[]) {
  const r1 = runs[1]?.dataset_hash === upload.dataset_hash ? runs[1] : undefined;
  const r2 = runs[2]?.dataset_hash === upload.dataset_hash ? runs[2] : undefined;
  const s1 = r1?.summary as Lever1Summary | undefined;
  const s2 = r2?.summary as Lever2RunSummary | undefined;
  const ann = (p: number | undefined | null) => (p === undefined || p === null ? null : annualise(p, upload.date_from, upload.date_to));
  // Levers 1 and 2 are counted once per parcel (engine combineLevers12).
  const overcharge = s1?.overchargePence ?? null;
  const bandSaving = s2 ? s2.combined.bandSavingPence : s1 ? s1.nearBandSavingPence : null;
  const levers12Period = s2 ? s2.combined.totalPence : s1 ? s1.overchargePence + s1.nearBandSavingPence : null;
  const levers12Annual = ann(levers12Period);
  const manualAnnual = manual.reduce((sum, x) => sum + (x.annual_saving_pence ?? 0), 0);
  const totalAnnual = levers12Annual === null ? null : levers12Annual + manualAnnual;
  return { r1, r2, s1, s2, ann, overcharge, bandSaving, levers12Period, totalAnnual };
}

/** Our fees for year one and each year after, from what is entered on the audit. Missing fees are named. */
export function feeTotals(audit: Pick<AuditRow, "audit_fee_pence" | "implementation_fee_pence" | "retainer_fee_pence">) {
  const auditFee = audit.audit_fee_pence;
  const implFee = audit.implementation_fee_pence;
  const retainer = audit.retainer_fee_pence;
  const missing = [auditFee === null ? "audit fee" : "", implFee === null ? "implementation fee" : "", retainer === null ? "retainer" : ""].filter(Boolean);
  const oneOff = (auditFee ?? 0) + (implFee ?? 0);
  const retainerYear = (retainer ?? 0) * 12;
  return { auditFee, implFee, retainer, missing, oneOff, retainerYear, yearOne: oneOff + retainerYear, yearTwo: retainerYear };
}

const listWords = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

export function buildReportModel(input: ReportInput): ReportModel {
  const { audit, upload, runs, manual, actions, block, baseline } = input;
  const { r1, r2, s1, s2, ann, overcharge, bandSaving, levers12Period, totalAnnual } = savingTotals(upload, runs, manual);
  const m = (lever: 3 | 4 | 5) => manual.find((x) => x.lever === lever);
  const period = `${day(upload.date_from)} to ${day(upload.date_to)}`;
  const client = audit.client_name || "the client";
  const sampleCards = block.rateCards.some((c) => c.startsWith("sample-"));
  const fees = feeTotals(audit);
  const air = baseline?.air ?? null;

  // ---- Costs today: shipping from the data, plus packaging when its cost per parcel was given.
  const packPerParcel = (r2?.options as { currentPackagingPence?: number | null } | undefined)?.currentPackagingPence ?? null;
  const shippingYear = baseline?.annualSpendPence ?? null;
  const packagingYear = packPerParcel !== null && baseline ? Math.round(packPerParcel * baseline.parcelsPerMonth * 12) : null;
  const costToday = shippingYear === null ? null : shippingYear + (packagingYear ?? 0);
  const netYearOne = totalAnnual === null ? null : totalAnnual - fees.yearOne;
  const netYearTwo = totalAnnual === null ? null : totalAnnual - fees.yearTwo;
  const paybackText = payback(fees.oneOff, netYearTwo);
  const shareOfCost = totalAnnual !== null && costToday ? totalAnnual / costToday : null;
  const feesNote = fees.missing.length ? ` The ${listWords(fees.missing)} ${fees.missing.length === 1 ? "is" : "are"} not set yet, so ${fees.missing.length === 1 ? "it is" : "they are"} left out.` : "";

  const sections: Section[] = [];

  // ================================================================ 1. What you spend now
  const spend: Block[] = [
    { kind: "motto", motto: MOTTOS.spend },
    { kind: "p", text: `Your shipping as it stands, from ${plural(upload.parcel_count, "parcel")} sent ${period}. Every saving in this report is measured against these figures.` },
  ];
  if (baseline) {
    spend.push({
      kind: "table",
      headers: ["Measure", "Your figure"],
      numeric: [1],
      widths: [5470, 3600],
      small: true,
      rows: [
        ["Parcels in an average month", n(baseline.parcelsPerMonth)],
        ["Shipping spend in the data period, ex VAT", gbp(baseline.spendPence)],
        ["Shipping spend, a year (estimate)", whole(baseline.annualSpendPence)],
        ...(packagingYear !== null ? [["Packaging, a year (estimate)", `${whole(packagingYear)} (${gbp(packPerParcel)} a parcel)`]] : []),
        ["Average cost per parcel", gbp(baseline.avgCostPence)],
        ["Middle (median) cost per parcel", gbp(baseline.medianCostPence)],
        ["Average parcel weight", `${baseline.avgWeightKg.toLocaleString("en-GB", { maximumFractionDigits: 2 })} kg`],
        ["Parcels with more than one item", pct(Math.round(baseline.multiItemShare * 1000), 1000)],
      ],
    });
    if (baseline.services.length) {
      spend.push({ kind: "h2", text: "By carrier service" });
      spend.push({
        kind: "table",
        headers: ["Service", "Parcels", "Share", "Spend", "Average"],
        numeric: [1, 2, 3, 4],
        widths: [3070, 1300, 1100, 1900, 1700],
        small: true,
        rows: baseline.services.slice(0, 4).map((s) => [s.service, n(s.parcels), pct(s.parcels, baseline.parcels), gbp(s.spendPence), gbp(s.avgCostPence)]),
      });
    }
  } else {
    spend.push({ kind: "note", lines: ["The starting point could not be worked out: the cleaned data was not available when this draft was made."] });
  }
  const bands = s1 ? Object.entries(s1.byBand).sort((a, b) => b[1].chargedPence - a[1].chargedPence).slice(0, 4) : [];
  if (bands.length && s1) {
    spend.push({ kind: "h2", text: "By price band" });
    spend.push({
      kind: "table",
      headers: ["Service and band", "Parcels", "Share of spend", "Charged"],
      numeric: [1, 2, 3],
      widths: [3670, 1400, 2000, 2000],
      small: true,
      rows: bands.map(([k, v]) => [k.replace("/", ", band "), n(v.count), pct(v.chargedPence, s1.chargedPence), gbp(v.chargedPence)]),
    });
  }
  spend.push({ kind: "h2", text: "The data we used" });
  spend.push({
    kind: "table",
    headers: ["Item", "Detail"],
    widths: [3070, 6000],
    small: true,
    rows: [
      ["File and dates", `${upload.file_name}, ${period}`],
      ["Rows used", `${n(upload.kept_rows)} of ${n(upload.source_rows)}${upload.source_rows > upload.kept_rows ? `; ${n(upload.source_rows - upload.kept_rows)} left out for missing or invalid values, or exact duplicates` : ""}`],
      ["Prices compared against", block.rateCards.join(", ") || "no rate card"],
    ],
  });
  sections.push({ heading: "What you spend now", blocks: spend });

  // ================================================================ 2. Where the extra cost comes from
  const extra: Block[] = [{ kind: "motto", motto: MOTTOS.extra }];
  if (!s1) {
    extra.push({ kind: "note", lines: ["Lever 1 has not been run on the current data."] });
  } else {
    extra.push({
      kind: "p",
      text: "Extra cost comes in two kinds. Billing errors are charges above the carrier's own price: you can claim them back and stop them. The rest is built into how parcels are packed and sent: it stops when the packing changes.",
    });
    extra.push({
      kind: "table",
      headers: ["To claim back from the carrier, a year", "To stop at source, a year"],
      numeric: [0, 1],
      rows: [[whole(ann(overcharge)), whole(ann(bandSaving))]],
    });
    const bandParcels = s2 ? s2.combined.parcelsWithSaving - s1.overchargedCount : s1.nearBandCount;
    extra.push({
      kind: "table",
      headers: ["Where it comes from", "Parcels", "In the period", "A year", "What to do"],
      numeric: [1, 2, 3],
      widths: [3000, 1100, 1450, 1350, 2170],
      rows: [
        ["Charged above the rate card", `${n(s1.overchargedCount)} (${pct(s1.overchargedCount, s1.parcels)})`, gbp(overcharge), whole(ann(overcharge)), "Claim back, then check each invoice"],
        ["Sent in a higher band than needed", n(Math.max(bandParcels, 0)), gbp(bandSaving), whole(ann(bandSaving)), "Pack to drop a band"],
        ["Charged on size, not weight", `${n(s1.volumetricCount)} (${pct(s1.volumetricCount, s1.parcels)})`, "–", "–", "Smaller box for the product"],
        ...(air ? [["Empty space in one-product parcels", n(air.parcelsMeasured), `${Math.round(air.airShare * 100)}% of volume`, "–", "Right-size the format"]] : []),
        ...(s1.unknownServiceCount + s1.noBandCount > 0 ? [["Not priced: service or size not on a rate card", n(s1.unknownServiceCount + s1.noBandCount), "–", "–", "Send us the rate card"]] : []),
      ],
    });
    // The largest single findings, from both levers, so each figure can be traced to an order.
    const fromL1 = ((r1?.results ?? []) as Lever1ParcelResult[])
      .filter((r) => r.flags.includes("overcharged") || r.nearBand)
      .map((r) => ({
        save: Math.max(r.differencePence ?? 0, 0) + (r.nearBand?.savingPence ?? 0),
        row: [
          r.orderId,
          gbp(r.chargedPence),
          gbp(r.expectedPence),
          [
            r.flags.includes("overcharged") ? `Charged ${gbp(r.differencePence)} above the rate card` : "",
            r.nearBand ? `${r.nearBand.bandName} would save ${gbp(r.nearBand.savingPence)} (over by ${overBy(r.nearBand.overBy)})` : "",
          ].filter(Boolean).join(". "),
        ],
      }));
    const fromL2 = ((r2?.results ?? []) as Lever2ParcelResult[])
      .filter((r) => r.status === "saving" && r.proposed)
      .map((r) => {
        const save = r.netSavingPence ?? r.postageSavingPence;
        return {
          save,
          row: [r.orderId, gbp(r.currentPostagePence), gbp(r.proposed!.postagePence), `${r.sku ?? "Product"} fits a ${r.proposed!.formatName.toLowerCase()}, band ${r.proposed!.bandCode}: saves ${gbp(save)}`],
        };
      });
    // Three of each kind, each order once.
    const seen = new Set<string>();
    const top = (xs: { save: number; row: string[] }[], k: number) =>
      [...xs].sort((a, b) => b.save - a.save).filter((x) => !seen.has(x.row[0]!) && seen.add(x.row[0]!)).slice(0, k);
    const examples = [...top(fromL1, 3), ...top(fromL2, 3)];
    if (examples.length) {
      extra.push({ kind: "h2", text: "Examples from your orders" });
      extra.push({ kind: "table", headers: ["Order", "Charged", "Should cost", "What we found"], numeric: [1, 2], widths: [1300, 1200, 1300, 5270], rows: examples.map((e) => e.row), small: true });
    }
  }
  extra.push({
    kind: "note",
    lines: [
      "Not checked in this audit: carrier surcharges (such as fuel and energy, peak and manual handling), late delivery and failed delivery claims, and returns. They appear on your carrier invoices; ask us if you want them checked.",
    ],
  });
  sections.push({ heading: "Where the extra cost comes from", blocks: extra });

  // ================================================================ 3. Where you could save
  const manualCell = (l: 3 | 4 | 5) => (m(l)?.annual_saving_pence != null ? whole(m(l)!.annual_saving_pence) : "not assessed");
  const sure = (l: 3 | 4 | 5) => (m(l)?.annual_saving_pence != null ? "Indicative" : "–");
  const save: Block[] = [{ kind: "motto", motto: MOTTOS.save }];
  save.push({
    kind: "p",
    text: totalAnnual === null
      ? "The data covers less than 28 days, which is too short to scale to a year."
      : `About ${whole(totalAnnual)} a year across the five levers${shareOfCost !== null ? `, or ${pct1(shareOfCost)} of what you spend today` : ""}. In the order we work through them:`,
  });
  save.push({
    kind: "table",
    headers: ["Lever", "A year (estimate)", "How sure we are", "Effort", "When"],
    numeric: [1],
    widths: [3370, 1500, 1500, 1100, 1600],
    small: true,
    rows: [
      [`1. ${LEVERS[0].name}: billing errors`, whole(ann(overcharge)), s1 ? "High" : "Not yet run", "Low", "Week 1"],
      ["1 and 2. Cheaper bands: near-band and right-sizing", whole(ann(bandSaving)), s1 || s2 ? "Medium" : "Not yet run", "Medium", "Weeks 1 to 3"],
      [`3. ${LEVERS[2].name}`, manualCell(3), sure(3), "Medium", "Weeks 3 to 4"],
      [`4. ${LEVERS[3].name}`, manualCell(4), sure(4), "Low", "Weeks 4 to 5"],
      [`5. ${LEVERS[4].name}`, manualCell(5), sure(5), "Medium", "Weeks 5 to 6"],
      ["Total", whole(totalAnnual), "", "", ""],
    ],
  });
  const barRows = [
    { label: "1  Billing errors", value: ann(overcharge) ?? 0 },
    { label: "1–2  Cheaper bands", value: ann(bandSaving) ?? 0 },
    { label: "3  Materials and levies", value: m(3)?.annual_saving_pence ?? 0 },
    { label: "4  Suppliers", value: m(4)?.annual_saving_pence ?? 0 },
    { label: "5  Carriers", value: m(5)?.annual_saving_pence ?? 0 },
  ].map((r) => ({ ...r, text: r.value > 0 ? `${whole(r.value)} a year` : "–" }));
  if (barRows.some((r) => r.value > 0)) save.push({ kind: "bars", rows: barRows });
  save.push({
    kind: "p",
    text: "High: your own charges against dated prices. Medium: modelled from parcel and product sizes, then confirmed by a packing trial. Indicative: worked by hand; depends on supplier and carrier replies. A parcel both levers 1 and 2 would move is counted once.",
  });
  save.push({ kind: "h2", text: "What we recommend" });
  save.push({ kind: "note", lines: [RECOMMENDATIONS_PLACEHOLDER] });
  const ppt = PLASTIC_PACKAGING_TAX;
  const epr = PACKAGING_EPR;
  const millions = (p: number) => `£${p / 100_000_000} million`;
  const eu = EU_EMPTY_SPACE;
  const euPct = Math.round(eu.maxShare * 100);
  const euMeasured = air
    ? `Your one-product parcels are about ${Math.round(air.airShare * 100)}% empty (our estimate)${({ over: ": over the limit", near: ": close to the limit", within: ": within it" } as const)[emptySpaceStanding(air.airShare)]}.`
    : "Not measured in this audit: we had no product sizes.";
  save.push({ kind: "h2", text: `Packaging rules to check (checked ${day(COMPLIANCE_CHECKED_ON)})` });
  save.push({
    kind: "table",
    headers: ["Rule", "Who it applies to", "What it costs"],
    widths: [2000, 3770, 3300],
    small: true,
    rows: [
      ["Plastic Packaging Tax", `Makers and importers of ${ppt.thresholdTonnes} tonnes or more of finished plastic packaging in 12 months, including filled imports.`, `${gbp(ppt.ratePerTonnePence)} a tonne from ${day(ppt.rateFrom)}, on packaging with less than ${Math.round(ppt.recycledContentExempt * 100)}% recycled plastic.`],
      ["Packaging EPR", `Large producers: turnover of ${millions(epr.largeProducer.turnoverPence)} or more, and over ${epr.largeProducer.tonnes} tonnes a year. Small producers (over ${millions(epr.smallProducer.turnoverPence)} and ${epr.smallProducer.tonnes} tonnes) report data only.`, `Large producers pay disposal fees. Packaging rated red costs ${epr.redToAmber} times amber in year 2, and packaging without enough recyclability information is treated as red.`],
      ["EU empty-space limit", `Anyone packing parcels for EU customers, from ${day(eu.from)}. Filler counts as empty space.`, `No more than ${euPct}% empty space in each parcel. ${euMeasured}`],
    ],
  });
  sections.push({ heading: "Where you could save", blocks: save });

  // ================================================================ 4. The plan, and going ahead
  const start = audit.implementation_starts;
  const plan: Block[] = [{ kind: "motto", motto: MOTTOS.plan }];
  plan.push({
    kind: "p",
    text: netYearOne !== null && netYearOne > 0
      ? `We recommend going ahead. The savings cover our fees ${paybackText === "straight away" ? "straight away" : `in ${paybackText}`}, and implementation takes up to ${IMPLEMENTATION_WEEKS} weeks from kickoff. We make the changes with your team and check each one on your real invoices. Longer-lead items, such as custom packaging, continue into the retainer.`
      : `Implementation takes up to ${IMPLEMENTATION_WEEKS} weeks from kickoff. We make the changes with your team and check each one on your real invoices. Longer-lead items, such as custom packaging, continue into the retainer.`,
  });
  const formatName = (code: string | null) =>
    code === null ? null : ((r2?.results ?? []) as Lever2ParcelResult[]).find((r) => r.proposed?.formatCode === code)?.proposed?.formatName.toLowerCase() ?? code;
  // Week by week: the agreed actions if there are any, otherwise a plan drawn from the findings.
  const planStart = start ?? "2026-01-05";
  const source = actions.length
    ? sortActions(actions)
    : suggestedActions({
        start: planStart,
        lever1: s1 ? { overchargedCount: s1.overchargedCount, nearBandCount: s1.nearBandCount } : null,
        lever2: s2 ? { savingCount: s2.savingCount, topFormat: formatName(Object.entries(s2.byFormat).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null) } : null,
        manual,
      });
  const weekOf = (due: string | null) => (due ? Math.min(Math.max(Math.ceil((toMs(due) - toMs(planStart)) / (7 * DAY)), 1), IMPLEMENTATION_WEEKS) : null);
  const rows = source
    .map((a) => ({ week: weekOf(a.due), lever: a.lever, title: a.title, owner: a.owner }))
    .sort((a, b) => (a.week ?? 99) - (b.week ?? 99) || (a.lever ?? 9) - (b.lever ?? 9));
  const timeline: string[][] = [
    ["Week 1", "Kickoff: agree the plan, the contacts and the dates", "All", "Ryoma and you"],
    ...rows.slice(0, 6).map((r) => [r.week ? `Week ${r.week}` : "To set", r.title, r.lever ? String(r.lever) : "All", r.owner || (r.lever === null ? "Ryoma" : "–")]),
    ["After", "Longer-lead items, such as custom packaging, and monthly checks", "All", "Retainer"],
  ];
  plan.push({ kind: "h2", text: start ? `Week by week: ${day(start)} to ${day(audit.implementation_ends)}` : `Week by week, over up to ${IMPLEMENTATION_WEEKS} weeks` });
  plan.push({ kind: "table", headers: ["When", "What happens", "Lever", "Who"], widths: [1100, 5170, 900, 1900], rows: timeline, small: true });
  plan.push({ kind: "h2", text: "How we measure success" });
  plan.push({
    kind: "table",
    headers: ["Measure", "Today", "How we check it"],
    numeric: [1],
    widths: [3470, 1700, 3900],
    small: true,
    rows: [
      ["Average shipping cost per parcel", baseline ? gbp(baseline.avgCostPence) : "–", "Carrier invoices, each month"],
      ["Parcels charged above the rate card", s1 ? `${n(s1.overchargedCount)} (${pct(s1.overchargedCount, s1.parcels)})` : "–", "Charges set against the rate card"],
      ["Parcels charged on size, not weight", s1 ? `${n(s1.volumetricCount)} (${pct(s1.volumetricCount, s1.parcels)})` : "–", "Chargeable weight on invoices"],
      ["Damage and returns from packaging", "you to provide", "Your returns records after each change"],
    ],
  });
  plan.push({ kind: "h2", text: "To go ahead" });
  plan.push({
    kind: "list",
    items: [
      `Tell us which recommendations to take forward: reply to ${COMPANY.email}.`,
      `We agree a kickoff date${start ? ` (we have pencilled in ${day(start)})` : ""}, and you name a contact for packing and one for carrier accounts.`,
      "You send what you pay for packaging today and the sizes of any products we could not assess.",
      `In week ${IMPLEMENTATION_WEEKS} we check the savings on your first invoices after the changes, and the retainer begins.`,
    ],
  });
  sections.push({ heading: "The plan, and going ahead", blocks: plan });

  // ================================================================ 5. Before and after
  const result: Block[] = [{ kind: "motto", motto: MOTTOS.result }];
  if (totalAnnual === null || costToday === null) {
    result.push({ kind: "note", lines: ["Before and after figures need a data period of at least 28 days and the cleaned data. They will appear once both are in place."] });
  } else {
    const after1 = costToday - totalAnnual + fees.yearOne;
    const after2 = costToday - totalAnnual + fees.yearTwo;
    result.push({
      kind: "p",
      text: `What you pay today, set against what you would pay after the savings and all of our fees. Year one is the first 12 months after implementation.${feesNote}`,
    });
    result.push({
      kind: "table",
      headers: ["", "Year one", "Each year after"],
      numeric: [1, 2],
      widths: [4670, 2200, 2200],
      small: true,
      rows: [
        [packagingYear !== null ? "Shipping and packaging today" : "Shipping today", whole(costToday), whole(costToday)],
        ["Saving across the five levers", whole(-totalAnnual), whole(-totalAnnual)],
        ["Audit fee (one-off)", fees.auditFee === null ? "not set" : whole(fees.auditFee), "–"],
        ["Implementation (one-off)", fees.implFee === null ? "not set" : whole(fees.implFee), "–"],
        ["Retainer", fees.retainer === null ? "not set" : `${whole(fees.retainerYear)} (${whole(fees.retainer)} a month)`, fees.retainer === null ? "not set" : whole(fees.retainerYear)],
        ["What you would pay, fees included", whole(after1), whole(after2)],
        ["Your saving after all of our fees", whole(netYearOne), whole(netYearTwo)],
      ],
    });
    // Saving against our fees, so the gap between them (what you keep) is plain to see.
    result.push({
      kind: "bars",
      rows: [
        { label: "Year one: saving", value: totalAnnual, text: whole(totalAnnual) },
        { label: "Year one: our fees", value: fees.yearOne, text: fees.missing.length ? `${whole(fees.yearOne)} so far` : whole(fees.yearOne) },
        { label: "Later years: saving", value: totalAnnual, text: whole(totalAnnual) },
        { label: "Later years: retainer", value: fees.yearTwo, text: fees.retainer === null ? "not set" : whole(fees.yearTwo) },
      ],
    });
    result.push({
      kind: "table",
      headers: ["One-off fees pay back in", "Saving after fees, two years", "Each £1 in fees returns"],
      numeric: [0, 1, 2],
      rows: [[paybackText, whole((netYearOne ?? 0) + (netYearTwo ?? 0)), fees.yearOne + fees.yearTwo > 0 ? gbp(Math.round(((totalAnnual * 2) / (fees.yearOne + fees.yearTwo)) * 100)) : "–"]],
    });
  }
  const assumptions = [
    "Estimates from your own data, not promises. Prices exclude VAT and come from the dated rate cards named on page 1; if your contract differs, the figures change.",
    upload.date_from && upload.date_to ? "A year scales the data period up and assumes volumes and mix stay the same. Busy seasons move it either way." : "Annual figures need order dates.",
    "Right-sizing is confirmed by a packing trial before a full switch. Parcels with more than one product are reviewed by hand.",
  ];
  if (sampleCards) assumptions.unshift("SAMPLE RATE CARDS WERE USED. These figures are for testing only and must not be sent to a client.");
  result.push({ kind: "h2", text: "What the figures rest on" });
  result.push({ kind: "list", items: assumptions });
  result.push({
    kind: "signoff",
    lines: [
      REVIEW_PLACEHOLDER,
      `${versionLines(block).filter((l) => !l.startsWith("Parcels analysed")).join(". ")}.`,
      `${COMPANY.legalName} · ${COMPANY.email} · ${COMPANY.web}. Registered in ${COMPANY.registeredIn}, company number ${COMPANY.companyNumber}. Registered office: ${COMPANY.registeredOffice}.`,
    ],
  });
  sections.push({ heading: "Before and after", blocks: result });

  // ================================================================ Cover
  const cover: Block[] = [];
  if (totalAnnual !== null) {
    cover.push({ kind: "hero", value: `About ${whole(totalAnnual)} a year`, label: `Estimated saving${shareOfCost !== null ? `, ${pct1(shareOfCost)} of what you spend today` : ""}.` });
  }
  cover.push({
    kind: "table",
    headers: ["You spend now, a year", "Saving a year", "Year one, after all fees", "Fees pay back in"],
    numeric: [0, 1, 2, 3],
    rows: [[whole(costToday), whole(totalAnnual), whole(netYearOne), totalAnnual === null ? "–" : paybackText]],
  });
  const findings: string[] = [];
  if (s1 && s1.overchargedCount > 0) findings.push(`${plural(s1.overchargedCount, "parcel")} charged above the carrier's own price: about ${whole(ann(s1.overchargePence))} a year to claim back.`);
  if (air && air.airShare >= 0.05) findings.push(`About ${Math.round(air.airShare * 100)}% of the space in your one-product parcels is empty: you pay to ship air.`);
  if (s2 && s2.savingCount > 0) findings.push(`${plural(s2.savingCount, "parcel")} could drop to a cheaper band in a better-fitting format.`);
  else if (s1 && s1.nearBandCount > 0) findings.push(`${plural(s1.nearBandCount, "parcel")} sit just over a cheaper band.`);
  findings.push(`Implementation takes up to ${IMPLEMENTATION_WEEKS} weeks${start ? `, from ${day(start)}` : " from kickoff"}.`);
  cover.push({ kind: "list", items: findings.slice(0, 4) });

  return {
    documentName: `Audit report: ${client}`,
    title: "Shipping and packaging audit",
    description: `${client}: ${plural(upload.parcel_count, "parcel")}, ${period}`,
    versionLine: `Draft ${input.draftNumber}  ·  ${monthYear(block.generatedAt)}  ·  Confidential to ${client} and Ryoma Insight`,
    summaryLabel: "WHAT THIS IS",
    summary: [
      `Our audit of ${client}'s shipping and packaging costs: what you spend now, where the extra cost comes from, where you could save, how we would put it right, and where you end up after our fees.`,
      totalAnnual === null
        ? s1 || s2 ? "The data period was too short to scale savings to a year." : "The levers have not been run on the current data, so there is no saving to show yet."
        : `Estimated saving: about ${whole(totalAnnual)} a year${netYearOne !== null && fees.yearOne > 0 ? `, or ${whole(netYearOne)} in year one after ${fees.missing.length ? "the fees set so far" : "all of our fees"}` : ""}.`,
    ],
    cover,
    preparedFor: client,
    preparedBy: COMPANY.legalName,
    contact: `${COMPANY.email} · ${COMPANY.web}`,
    sections,
  };
}
